import { FiscalRegime, LogoUrls } from '~/types/company';
import {
  DocumentDirection,
  DocumentFormat,
  DocumentSource,
  DocumentStatus,
  DocumentType,
  DocumentVATCollection,
  EInvoiceType,
  WithholdingTaxType,
} from '~/types/document';
import { Document } from '~/types/document';
import { DocumentAttachment } from '~/types/document-attachment';
import { TaxableMode } from '~/types/document-builder';
import { InvoicePaymentFlow } from '~/types/flow';
import { Money } from '~/types/money';
import { Counterpart } from './counterparts';
import { CountryIsoCode } from './country';

export const InvoicesQueryKeys = {
  all: ['Invoices'],
  invoice: (invoiceId: Document['id']) => ['Invoices', { invoiceId }],
  eInvoice: (documentId: Document['id']) => ['EInvoice', { documentId }],
  eBill: (documentId: Document['id']) => ['EBill', { documentId }],
  countByStatus: (statuses: DocumentStatus[], documentTypes?: DocumentType[]) => [
    'Invoices',
    'countByStatus',
    { statuses, documentTypes },
  ],
  countByStatusAll: ['Invoices', 'countByStatus'],
  sharedInvoice: (token: string) => ['SharedInvoice', { token }],
  sharedInvoiceAll: ['SharedInvoice'],
};

export const InvoicesMutationKeys = {
  saveInvoice: ['saveInvoice'],
};

// Invoices - Fattura FPX12
export interface ItalianEInvoice {
  versione: string;
  fatturaElettronicaHeader: FatturaHeader;
  fatturaElettronicaBody: FatturaBody[];
}

// Simplified invoices - Fattura Semplificata FSM10
export interface FSM10EInvoice {
  versione: 'FSM10';
  fatturaElettronicaHeader: FSM10Header;
  fatturaElettronicaBody: FSM10Body[];
}

export const isFSM10EInvoice = (invoice: ItalianEInvoice): boolean => invoice.versione === 'FSM10';

// FSM10 header — cedentePrestatore has flat fields, cessionarioCommittente has a different shape
interface FSM10Header {
  datiTrasmissione: DatiTrasmissione;
  cedentePrestatore: FSM10CedentePrestatore;
  cessionarioCommittente: FSM10CessionarioCommittente;
  soggettoEmittente: string | null;
}

export interface FSM10CedentePrestatore {
  nome: string | null;
  cognome: string | null;
  denominazione: string | null;
  codiceFiscale: string | null;
  idFiscaleIva: { idPaese: string; idCodice: string } | null;
  regimeFiscale: FiscalRegime | null;
  alboProfessionale: string | null;
  provinciaAlbo: string | null;
  numeroIscrizioneAlbo: string | null;
  dataIscrizioneAlbo: string | null;
  sede: FSM10Sede | null;
}

export interface FSM10CessionarioCommittente {
  identificativiFiscali: {
    codiceFiscale: string | null;
    idFiscaleIva: { idPaese: string; idCodice: string } | null;
  } | null;
  altriDatiIdentificativi: {
    nome: string | null;
    cognome: string | null;
    denominazione: string | null;
    sede: FSM10Sede | null;
  } | null;
}

interface FSM10Sede {
  indirizzo: string | null;
  numeroCivico: string | null;
  cap: string | null;
  comune: string | null;
  provincia: string | null;
  nazione: string | null;
}

// FSM10 body — datiBeniServizi is an array of line items, not an object
export interface FSM10Body {
  datiGenerali: {
    datiGeneraliDocumento: FSM10DatiGeneraliDocumento;
    datiFatturaRettificata: FSM10DatiFatturaRettificata | null;
  };
  datiBeniServizi: FSM10DatiBeniServizi[];
  allegati: FSM10Allegato[] | null;
}

// Simplified invoices carry no document total; it is derived from the line items.
interface FSM10DatiGeneraliDocumento {
  tipoDocumento: EInvoiceType;
  data: string;
  numero: string | null;
  divisa: string;
}

interface FSM10DatiFatturaRettificata {
  dataFr: string | null;
  numeroFr: string | null;
  elementiRettificati: string | null;
}

interface FSM10DatiBeniServizi {
  descrizione: string | null;
  importo: string;
  natura: string | null;
  riferimentoNormativo: string | null;
  datiIva: {
    aliquota: string | null;
    imposta: string | null;
  } | null;
}

interface FSM10Allegato {
  nomeAttachment: string | null;
  algoritmoCompressione: string | null;
  formatoAttachment: string | null;
  descrizioneAttachment: string | null;
  attachment: string | null;
}

export interface Anagrafica {
  denominazione: string | null;
  nome: string | null;
  cognome: string | null;
  titolo: string | null;
  codEori: string | null;
}

interface ParteFattura {
  datiAnagrafici: {
    anagrafica: Anagrafica;
    codiceFiscale: string | null;
  };
  sede: {
    cap: string;
    comune: string;
    indirizzo: string;
    nazione: string;
    numeroCivico: string | null;
    provincia: string | null;
  };
}

interface IdFiscaleIva {
  idPaese: string;
  idCodice: string;
}

export interface CessionarioCommittente extends ParteFattura {
  datiAnagrafici: ParteFattura['datiAnagrafici'] & {
    idFiscaleIva: IdFiscaleIva | null;
  };
}

export interface CedentePrestatore extends ParteFattura {
  datiAnagrafici: ParteFattura['datiAnagrafici'] & {
    idFiscaleIva: IdFiscaleIva;
    regimeFiscale: FiscalRegime;
    alboProfessionale: string | null;
    provinciaAlbo: string | null;
    numeroIscrizioneAlbo: string | null;
    dataIscrizioneAlbo: string | null;
  };
  riferimentoAmministrazione: string | null;
  contatti: {
    telefono: string | null;
    fax: string | null;
    email: string | null;
  } | null;
}

interface TerzoIntermediarioOSoggettoEmittente {
  datiAnagrafici: {
    anagrafica: Anagrafica;
    codiceFiscale: string | null;
    idFiscaleIva: IdFiscaleIva;
  };
}

export interface DatiTrasmissione {
  progressivoInvio: string | null;
  formatoTrasmissione: 'FPA12' | 'FPR12' | 'FSM10';
  codiceDestinatario: string;
  idTrasmittente: {
    idPaese: string;
    idCodice: string;
  } | null;
  pecDestinatario: string | null;
  contattiTrasmittente: {
    email: string | null;
    telefono: string | null;
  };
}

export interface FatturaHeader {
  cessionarioCommittente: CessionarioCommittente;
  cedentePrestatore: CedentePrestatore;
  terzoIntermediarioOSoggettoEmittente: TerzoIntermediarioOSoggettoEmittente | null;
  datiTrasmissione: DatiTrasmissione;
  soggettoEmittente: string | null;
}

export interface DatiGeneraliDocumento {
  arrotondamento: string | null;
  causale: string[] | null;
  data: string;
  datiBollo: DatiBollo | null;
  datiCassaPrevidenziale: DatiCassaPrevidenziale[];
  datiRitenuta: DatiRitenuta[];
  divisa: string;
  importoTotaleDocumento: string | null;
  numero: string | null;
  scontoMaggiorazione: ScontoMaggiorazione[];
  tipoDocumento: EInvoiceType;
  art73: string | null;
}

export interface FatturaBody {
  datiGenerali: {
    datiGeneraliDocumento: DatiGeneraliDocumento;
    datiContratto: RiferimentoDocumento[];
    datiConvenzione: RiferimentoDocumento[];
    datiDdt: DatiDDT[];
    datiFattureCollegate?: RiferimentoDocumento[];
    datiOrdineAcquisto: RiferimentoDocumento[];
    datiRicezione: RiferimentoDocumento[];
    fatturaPrincipale: null;
    datiTrasporto: DatiTrasporto | null;
    useGrossPrices: boolean | null;
  };
  datiPagamento: DatiPagamento[];
  datiBeniServizi: {
    datiRiepilogo: DatiRiepilogo[];
    dettaglioLinee: DettaglioLinea[];
  };
}

export interface DatiRitenuta {
  tipoRitenuta: WithholdingTaxType;
  importoRitenuta: string;
  aliquotaRitenuta: string;
  causalePagamento: string;
  taxableAmount?: string;
  taxableMode?: TaxableMode;
}

interface DatiBollo {
  bolloVirtuale: 'SI' | 'NO';
  importoBollo: string;
}

export interface DatiCassaPrevidenziale {
  tipoCassa: TipoCassa;
  alCassa: string;
  importoContributoCassa: string;
  imponibileCassa: string | null;
  aliquotaIva: string;
  ritenuta: 'SI' | null;
  natura: Natura | null;
  riferimentoAmministrazione: string | null;
}

export enum TipoCassa {
  AvvocatiEProcuratori = 'TC01',
  Commercialisti = 'TC02',
  Geometri = 'TC03',
  IngegneriArchitetto = 'TC04',
  Notariato = 'TC05',
  RagioneriPeriti = 'TC06',
  ENASARCO = 'TC07',
  ENPACL = 'TC08',
  ENPAM = 'TC09',
  ENPAF = 'TC10',
  ENPAV = 'TC11',
  ENPAIA = 'TC12',
  Marittime = 'TC13',
  INPGI = 'TC14',
  ONAOSI = 'TC15',
  CASAGIT = 'TC16',
  EEPI = 'TC17',
  EPAP = 'TC18',
  ENPAB = 'TC19',
  ENPAPI = 'TC20',
  ENPAP = 'TC21',
  INPS = 'TC22',
}

export enum StampType {
  Customer = 'CUSTOMER',
  Issuer = 'ISSUER',
}

export interface RiferimentoDocumento {
  riferimentoNumeroLinea: number | null;
  idDocumento: string;
  data: string | null;
  numItem: string | null;
  codiceCommessaConvenzione: string | null;
  codiceCup: string | null;
  codiceCig: string | null;
}

export interface DatiDDT {
  numeroDdt: string;
  dataDdt: string;
  riferimentoNumeroLinea: number | null;
}

export interface DatiPagamento {
  condizioniPagamento: CondizioniPagamento;
  dettaglioPagamento: DettaglioPagamento[];
}

export interface DettaglioPagamento {
  remoteId?: string | null; // this is just a workaround to update flow properly
  accountId: string | null;
  iban: string | null;
  modalitaPagamento: EInvoicePaymentMethod;
  importoPagamento: string;
  dataScadenzaPagamento: string | null;
  istitutoFinanziario: string | null;
  beneficiario?: string | null;
  abi?: string | null;
  cab?: string | null;
  bic?: string | null;
  codUfficioPostale?: string | null;
  titoloQuietanzante?: string | null;
  cognomeQuietanzante?: string | null;
  nomeQuietanzante?: string | null;
  cfQuietanzante?: string | null;
  dataLimitePagamentoAnticipato?: string | null;
  scontoPagamentoAnticipato?: string | null;
  dataDecorrenzaPenale?: string | null;
  penalitaPagamentiRitardati?: string | null;
  codicePagamento?: string | null;
  dataRiferimentoTerminiPagamento?: string | null;
  giorniTerminiPagamento?: string | null;
}

export enum EsigibilitaIva {
  Deferred = 'D',
  Immediate = 'I',
  Split = 'S',
}

export const naturaValues = [
  'N1',
  'N2.1',
  'N2.2',
  'N3.1',
  'N3.2',
  'N3.3',
  'N3.4',
  'N3.5',
  'N3.6',
  'N4',
  'N5',
  'N6.1',
  'N6.2',
  'N6.3',
  'N6.4',
  'N6.5',
  'N6.6',
  'N6.7',
  'N6.8',
  'N6.9',
  'N7',
] as const;

export type Natura = (typeof naturaValues)[number];

export interface DatiRiepilogo {
  aliquotaIva: string;
  arrotondamento: string | null;
  esigibilitaIva: EsigibilitaIva | null;
  imponibileImporto: string;
  imposta: string;
  natura: Natura | null;
  riferimentoNormativo: string | null;
  speseAccessorie: string | null;
}

export interface DettaglioLinea {
  descrizione: string;
  extendedDescription: string | null;
  quantita: string | null;
  ritenuta: string | null;
  natura: Natura | null;
  riferimentoNormativo?: string | null;
  scontoMaggiorazione: ScontoMaggiorazione[];
  aliquotaIva: string;
  numeroLinea: string;
  includiPrezzo: boolean | null;
  prezzoUnitario: string;
  prezzoTotale: string;
  unitaMisura: string | null;
  dataInizioPeriodo: string | null;
  dataFinePeriodo: string | null;
  codiceArticolo: CodiceArticolo[];
  altriDatiGestionali: AltriDatiGestionali[];
  grossPrice: string | null;
  riferimentoAmministrazione: string | null;
}

export enum TipoScontoMaggiorazione {
  Sconto = 'SC',
  Maggiorazione = 'MG',
}

interface ScontoMaggiorazione {
  tipo: TipoScontoMaggiorazione;
  importo: string | null;
  percentuale: string | null;
}

interface CodiceArticolo {
  codiceTipo: string;
  codiceValore: string;
}

interface AltriDatiGestionali {
  tipoDato: string;
  riferimentoTesto: string | null;
  riferimentoNumero: string | null;
  riferimentoData: string | null;
}

export interface DatiTrasporto {
  indirizzoResa: IndirizzoResa | null;
  causaleTrasporto: string | null;
  datiAnagraficiVettore: DatiAnagraficiVettore | null;
  numeroColli: string | null;
  pesoNetto: string | null;
}

interface DatiAnagraficiVettore {
  idFiscaleIva: IdFiscaleIva;
  anagrafica: AnagraficaVettore;
}

interface AnagraficaVettore {
  nome: string | null;
}

interface IndirizzoResa {
  indirizzo: string | null;
  cap: string | null;
  comune: string | null;
  nazione: CountryIsoCode | null;
}

export enum CondizioniPagamento {
  Installments = 'TP01',
  SinglePayment = 'TP02',
  PrePayment = 'TP03',
}

export interface SharedEInvoice {
  data: {
    document: {
      format: DocumentFormat;
      isEInvoice: boolean;
      notes: string | null;
      status: DocumentStatus;
      type: DocumentType;
      source: DocumentSource;
      number?: string;
      counterpartName: string;
      creationDate?: string;
      xmlUrl?: string;
    };
    attachments: DocumentAttachment[];
    company: {
      logoUrls: LogoUrls;
    };
    invoice: ItalianEInvoice;
    flows?: InvoicePaymentFlow[];
  };
}

export enum EInvoicePaymentMethod {
  Contanti = 'MP01',
  Assegno = 'MP02',
  AssegnoCircolare = 'MP03',
  ContantiPressoTesoreria = 'MP04',
  Bonifico = 'MP05',
  VagliaCambiario = 'MP06',
  BollettinoBancario = 'MP07',
  CartaDiPagamento = 'MP08',
  RID = 'MP09',
  RIDUtenze = 'MP10',
  RIDVeloce = 'MP11',
  RIBA = 'MP12',
  MAV = 'MP13',
  QuietanzaErario = 'MP14',
  GirocontoSpeciale = 'MP15',
  DomiciliazioneBancaria = 'MP16',
  DomiciliazionePostale = 'MP17',
  BollettinoPostale = 'MP18',
  SEPADirectDebit = 'MP19',
  SEPADirectDebitCORE = 'MP20',
  SEPADirectDebitB2B = 'MP21',
  Trattenuta = 'MP22',
  PagoPA = 'MP23',
}

export interface DocumentUpdateRequest {
  category: { id: string } | null;
  creationDate: string;
  counterpart: Counterpart | null;
  counterpartName?: string | null;
  direction: DocumentDirection;
  documentType: DocumentType;
  eInvoiceType: EInvoiceType | null;
  format: DocumentFormat | null;
  isEInvoice: boolean;
  notes: string | null;
  subcategory: { id: string } | null;
  vatCollection: DocumentVATCollection | null;
  related: { id: string | null }[];
}

export interface VatTotal {
  vatExemption: Natura | null;
  vatPercentage: string;
  netTotal: Money;
  vatTotal: Money;
  count: number;
  lawReference: string | null;
  rounding?: string;
}
export interface ItalianEBill {
  datiRt?: ItalianEBillDatiRt;
  datiDc?: ItalianEBillDatiDc;
  dataOraRilevazione: string | null; // only available for RT data
  trasmissione: ItalianEBillTrasmissione | null; // only available for RT data
  versione: string;
}

export interface ItalianEBillDatiRt {
  riepilogo: ItalianEBillRiepilogo[];
  totali: ItalianEBillTotali;
}

export interface ItalianEBillDatiDc {
  dataEmissione: string;
  dettaglioDocCom: ItalianEBillDocComDetails[];
  imponibileNonRiscosso: string;
  ivaIdCodice: string;
  pagatoContanti: string;
  pagatoElettronico: string;
  ticket: ItalianEBillTicket;
}

export interface ItalianEBillRiepilogo {
  importoParziale?: string;
  iva?: ItalianEBillIva;
  natura?: string;
  ventilazioneIva?: string;
  ammontare?: string;
  totaleAmmontareResi?: string;
  totaleAmmontareAnnulli?: string;
  beniInSospeso?: string;
  nonRiscossoServizi?: string;
  nonRiscossoFatture?: string;
  nonRiscossoOmaggio?: string;
  totaleDaFattureRT?: string;
}

interface ItalianEBillDocComDetails {
  imponibileVendite: string;
  iva: ItalianEBillIva;
}

interface ItalianEBillIva {
  aliquotaIva: string;
  imposta: string;
}

interface ItalianEBillTotali {
  pagatoContanti?: string;
  pagatoElettronico?: string;
  ticket?: ItalianEBillTicket;
  scontoApagare?: string;
  numeroDocCommerciali?: string;
}

interface ItalianEBillTicket {
  numeroTicket: number;
  pagatoTicket: string;
}

interface ItalianEBillTrasmissione {
  codiceFiscaleEsercente: string;
  dispositivo?: ItalianEBillDispositivo;
  formato: string;
  pivaEsercente: string;
  progressivo: string;
}

interface ItalianEBillDispositivo {
  idDispositivo: string;
}
